import { useMemo } from 'react'

import { useQueryParam } from '~publish/hooks/useQueryParam'

export type MultiSelectFilterState<T> = {
  onSelect: (value: string[]) => void
  selected: T[]
  selectedIds: string[]
  sanitized: string[]
  queryParams: undefined | string[]
}

const EMPTY_ARRAY: never[] = []

export const useMultiSelectFilter = <T extends { id: string }>(
  key: string,
  available: readonly T[],
): MultiSelectFilterState<T> => {
  const [rawQueryParams, setQueryParams] = useQueryParam<string[]>(key)
  // A key present without its `[]` suffix (e.g. `channels=x`, a hand-typed or
  // stale URL) parses as a plain string rather than an array — coerce it here
  // so every consumer of `queryParams` can trust it's an array, not a string
  // whose own `.filter`/`.includes` behave differently. See PUBLISH-FRONTEND-2BA.
  const queryParams = useMemo(
    () =>
      Array.isArray(rawQueryParams)
        ? rawQueryParams
        : rawQueryParams === undefined
          ? undefined
          : [rawQueryParams],
    [rawQueryParams],
  )
  const [sanitized, selected, selectedIds] = useMemo(() => {
    const isEmpty =
      queryParams === null ||
      queryParams === undefined ||
      queryParams.length === 0
    const filteredSelected = isEmpty
      ? EMPTY_ARRAY
      : available.filter((item) => queryParams?.includes(item.id))
    return [
      isEmpty ? EMPTY_ARRAY : queryParams,
      filteredSelected,
      filteredSelected.map((item) => item.id),
    ]
  }, [queryParams, available])

  return {
    sanitized,
    selected,
    selectedIds,
    queryParams,
    onSelect: (value: string[]) => setQueryParams(value),
  }
}
